<template>
  <b-col md="12" class="mb-3">
    <b-card class="formCardDash">
      <div class="card-header">
        <h6 class="card-title mb-0">Assignments</h6>
        <small class="text-muted">Total Assignments: {{ totalRecords }}</small>
      </div>
      <div class="card-body">
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by job no, client name..."
              @input="debounceSearch"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-form-select
              v-model="perPage"
              :options="[10, 25, 50, 100]"
              @change="updatePagination"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-button
              variant="outline-info"
              size="sm"
              @click="refreshData"
              :disabled="isLoading"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </b-button>
          </b-col>
        </b-row>

        <!-- Bootstrap Table with Pagination -->
        <b-table
          :items="assignmentsData"
          :fields="fields"
          :busy="isLoading"
          striped
          bordered
          hover
          small
          show-empty
          empty-text="No assignment data found"
          class="mb-0"
        >
          <template slot="table-busy">
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" small></b-spinner>
              <strong class="ml-2">Loading...</strong>
            </div>
          </template>

          <template slot="JobNo" slot-scope="data">
            <code>{{ data.value }}</code>
          </template>

          <template slot="JobDate" slot-scope="data">
            {{ formatDate(data.value) }}
          </template>

          <template slot="JobType" slot-scope="data">
            <b-badge :variant="getJobTypeVariant(data.value)">{{ data.value }}</b-badge>
          </template>

          <template slot="status" slot-scope="data">
            <b-badge :variant="getStatusVariant(data.value)">{{ getStatusText(data.value) }}</b-badge>
          </template>

          <template slot="ClientName" slot-scope="data">
            <strong>{{ data.value || 'Unknown' }}</strong>
            <br>
            <small class="text-muted">{{ data.item.EnqNo || '-' }}</small>
          </template>

          <template slot="POL" slot-scope="data">
            <small>{{ data.value }} → {{ data.item.POD }}</small>
          </template>

          <template slot="assignmentType" slot-scope="data">
            <b-badge :variant="getAssignmentTypeVariant(data.value)">{{ data.value }}</b-badge>
          </template>

          <template slot="actions" slot-scope="data">
            <b-button
              size="sm"
              variant="outline-info"
              @click.stop="toggleDetails(data.item)"
              class="toggle-btn"
            >
              <i :class="data.item._showDetails ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
            </b-button>
          </template>

          <!-- Row Details Slot -->
          <template slot="row-details" slot-scope="row">
            <div class="row-details">
              <!-- Job Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-primary">
                    <div class="card-header bg-primary text-white">
                      <i class="fa fa-briefcase"></i> Job Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Job No:</strong> {{ row.item.JobNo }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Date:</strong> {{ formatDate(row.item.JobDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Type:</strong> {{ row.item.JobType }}
                        </div>
                        <div class="col-md-3">
                          <strong>Transport Type:</strong> {{ row.item.JobTransportType }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Container Type:</strong> {{ row.item.JobTContainerType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Shipment Type:</strong> {{ row.item.JobShipmentType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised By:</strong> {{ row.item.RaisedBy }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised From:</strong> {{ row.item.RaisedFrom }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Stuffing Type:</strong> {{ row.item.stuffingType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Multi Container No:</strong> {{ row.item.multiConNo || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Branch ID:</strong> {{ row.item.BranchID }}
                        </div>
                        <div class="col-md-3">
                          <strong>Status:</strong> {{ getStatusText(row.item.status) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-success">
                    <div class="card-header bg-success text-white">
                      <i class="fa fa-map-marker"></i> Location Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-2">
                          <strong>POR:</strong> {{ row.item.POR || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>POL:</strong> {{ row.item.POL || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>POD:</strong> {{ row.item.POD || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>FPOD:</strong> {{ row.item.FPOD || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>Stuffing:</strong> {{ row.item.STUFF || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>Handover:</strong> {{ row.item.HAND || '-' }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Pick:</strong> {{ row.item.PICK || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>SOB Date:</strong> {{ formatDate(row.item.sob_date_detected) }}
                        </div>
                        <div class="col-md-3">
                          <strong>ETA Est.:</strong> {{ formatDate(row.item.ETA_destination_estimated) }}
                        </div>
                        <div class="col-md-3">
                          <strong>ETA Act.:</strong> {{ formatDate(row.item.ETA_destination_actual) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Client & Enquiry Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-warning">
                    <div class="card-header bg-warning">
                      <i class="fa fa-users"></i> Client & Enquiry Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Main Client:</strong> {{ row.item.ClientName || 'Unknown' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Exporter:</strong> {{ row.item.ExpClientName || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Importer:</strong> {{ row.item.ImpClientName || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Enquiry:</strong> {{ row.item.EnqNo || '-' }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Enquiry Date:</strong> {{ formatDate(row.item.EnqDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Shipping Line:</strong> {{ row.item.SlineName || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Login User:</strong> {{ row.item.LoginUserName }}
                        </div>
                        <div class="col-md-6">
                          <strong>Email:</strong> {{ row.item.LoginUserEmail }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Shipping & Billing Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-info">
                    <div class="card-header bg-info text-white">
                      <i class="fa fa-ship"></i> Shipping & Billing Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>MBL:</strong> {{ row.item.MBL || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>HBL:</strong> {{ row.item.HBL || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>BL Type:</strong> {{ row.item.BlType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Vessel/Voyage:</strong> {{ row.item.VesselVoyage || '-' }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Freight Terms MBL:</strong> {{ row.item.FreightTermsMbl || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Freight Terms HBL:</strong> {{ row.item.FreightTermsHbl || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Freight Terms Liner:</strong> {{ row.item.FreightTermsLiner || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>MBL Release Date:</strong> {{ formatDate(row.item.MBLReleaseDate) }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>MBL Date:</strong> {{ formatDate(row.item.MBLDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Particular Type:</strong> {{ row.item.ParticularType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Buying Rate:</strong> {{ formatCurrency(row.item.BuyingRate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Selling Rate:</strong> {{ formatCurrency(row.item.SellingRate) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Validation & Status Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-secondary">
                    <div class="card-header bg-secondary text-white">
                      <i class="fa fa-check-circle"></i> Validation & Status Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-2">
                          <strong>Expense Validation:</strong>
                          <b-badge :variant="row.item.is_expense_validation ? 'success' : 'danger'">
                            {{ row.item.is_expense_validation ? 'Yes' : 'No' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>Additional Task:</strong>
                          <b-badge :variant="row.item.is_additional_task_validation ? 'success' : 'danger'">
                            {{ row.item.is_additional_task_validation ? 'Yes' : 'No' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>SLine Validation:</strong>
                          <b-badge :variant="row.item.is_sline_validation ? 'success' : 'danger'">
                            {{ row.item.is_sline_validation ? 'Yes' : 'No' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>NOE Validation:</strong>
                          <b-badge :variant="row.item.is_noe_validation ? 'success' : 'danger'">
                            {{ row.item.is_noe_validation ? 'Yes' : 'No' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>All Active:</strong>
                          <b-badge :variant="row.item.isAllActive ? 'success' : 'danger'">
                            {{ row.item.isAllActive ? 'Yes' : 'No' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>Consolidation:</strong>
                          <b-badge :variant="row.item.isConsolidation ? 'success' : 'danger'">
                            {{ row.item.isConsolidation ? 'Yes' : 'No' }}
                          </b-badge>
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-2">
                          <strong>Final Sales Billing:</strong>
                          <b-badge :variant="row.item.isfinalsalesBillingComplete ? 'success' : 'danger'">
                            {{ row.item.isfinalsalesBillingComplete ? 'Complete' : 'Pending' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>TPT Invoice:</strong>
                          <b-badge :variant="row.item.tptInvoiceClosed ? 'success' : 'danger'">
                            {{ row.item.tptInvoiceClosed ? 'Closed' : 'Open' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>CC Invoice:</strong>
                          <b-badge :variant="row.item.ccInvoiceClosed ? 'success' : 'danger'">
                            {{ row.item.ccInvoiceClosed ? 'Closed' : 'Open' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>FF Invoice:</strong>
                          <b-badge :variant="row.item.ffInvoiceClosed ? 'success' : 'danger'">
                            {{ row.item.ffInvoiceClosed ? 'Closed' : 'Open' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>FF Locked:</strong>
                          <b-badge :variant="row.item.ff_is_locked ? 'warning' : 'success'">
                            {{ row.item.ff_is_locked ? 'Locked' : 'Unlocked' }}
                          </b-badge>
                        </div>
                        <div class="col-md-2">
                          <strong>TPT Locked:</strong>
                          <b-badge :variant="row.item.tpt_is_locked ? 'warning' : 'success'">
                            {{ row.item.tpt_is_locked ? 'Locked' : 'Unlocked' }}
                          </b-badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Subscription Tracking -->
              <div class="row mb-3" v-if="row.item.subscriptionTracking && row.item.subscriptionTracking.length > 0">
                <div class="col-12">
                  <div class="card border-danger">
                    <div class="card-header bg-danger text-white">
                      <i class="fa fa-rss"></i> Subscription Tracking ({{ row.item.subscriptionTracking.length }})
                    </div>
                    <div class="card-body p-0">
                      <b-table
                        :items="row.item.subscriptionTracking"
                        :fields="subscriptionFields"
                        small
                        bordered
                        class="mb-0"
                      >
                        <template slot="subscription_status_detials_status" slot-scope="data">
                          <b-badge :variant="data.value === 1 ? 'success' : 'warning'">
                            {{ data.value === 1 ? 'Active' : 'Inactive' }}
                          </b-badge>
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="totalItems > pagination.perPage"
          limit="25"
          align="center"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total-rows="totalItems"
          @change="onPageChange"
          class="mt-3"
        ></b-pagination>

        <!-- Table Info -->
        <div class="mt-2 d-flex justify-content-between align-items-center">
          <div>
            <b-form-select
              v-model="pagination.perPage"
              :options="pagination.pageOptions"
              @change="updatePagination"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">per page</span>
          </div>

          <div class="text-muted small">
            Total Records: {{ totalItems }}
          </div>
        </div>

      </div>
    </b-card>
  </b-col>
</template>

<script>
import moment from "moment";
import SalesPersonTeusService from "../../../services/SalesPersonTeusService";

export default {
  name: "AssignmentSection",
  props: {
    assignmentsData: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      isLoading: false,
      totalItems: 0,
      totalPages: 1,
      filter: null,
      pagination: {
        currentPage: 1,
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
      },
      fields: [
        { key: 'JobNo', label: 'Job No', sortable: true },
        { key: 'JobDate', label: 'Job Date', sortable: true },
        { key: 'JobType', label: 'Job Type', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'ClientName', label: 'Client Name', sortable: true },
        { key: 'POL', label: 'POL → POD', sortable: false },
        { key: 'assignmentType', label: 'Assignment Type', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ],
      subscriptionFields: [
        { key: 'request_key', label: 'Request Key' },
        { key: 'request_carrier_name', label: 'Carrier Name' },
        { key: 'fk_booking_id', label: 'Booking ID' },
        { key: 'subscription_status_detials_status', label: 'Status' }
      ],
    };
  },
  computed: {
    totalRecords() {
      return this.assignmentsData.length;
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "₹0";
      return "₹" + Number(value).toLocaleString("en-IN");
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD-MM-YYYY");
    },

    getJobTypeVariant(type) {
      if (!type) return 'secondary';
      const types = type.split(',');
      if (types.length > 1) return 'info'; // Multiple types

      const variants = {
        'CC': 'success',
        'FF': 'primary',
        'TPT': 'warning',
        'CC,TPT,FF': 'info',
        'CC,FF': 'success',
        'TPT,FF': 'warning'
      };
      return variants[type] || 'secondary';
    },

    getAssignmentTypeVariant(type) {
      const variants = {
        'CC': 'success',
        'FF': 'primary',
        'TPT': 'warning',
        'CC,TPT,FF': 'info',
        'CC,FF': 'success',
        'TPT,FF': 'warning'
      };
      return variants[type] || 'secondary';
    },

    getStatusVariant(status) {
      const variants = {
        'Active': 'success',
        'Inactive': 'danger',
        'Pending': 'warning',
        'Completed': 'info',
        'Cancelled': 'danger'
      };
      return variants[status] || 'secondary';
    },

    getStatusText(status) {
      const statusMap = {
        'Active': 'Active',
        'Inactive': 'Inactive',
        'Pending': 'Pending',
        'Completed': 'Completed',
        'Cancelled': 'Cancelled'
      };
      return statusMap[status] || status;
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filter = this.searchQuery;
      }, 500);
    },

    updatePagination() {
      this.pagination.currentPage = 1;
    },

    onFiltered(filteredItems) {
      // Trigger pagination reset when filtered
      this.pagination.currentPage = 1;
    },

    onPageChange(page) {
      this.pagination.currentPage = page;
      this.loadData();
    },

    toggleDetails(assignment) {
      // Toggle the _showDetails property for BootstrapVue row-details
      assignment._showDetails = !assignment._showDetails;
      this.$forceUpdate(); // Force update to trigger reactivity
    },

    async loadData() {
      this.isLoading = true;
      try {
        // For b-table with client-side pagination, we can load all data at once
        // or implement server-side pagination if needed
        const payload = {
          salesPersonId: this.$store.state.user.userId,
          clientId: this.$store.state.client.clientId || null,
          dateRange: this.$store.state.dateRange || '2025-2026',
          page: this.pagination.currentPage,
          limit: this.pagination.perPage,
          search: this.searchQuery
        };

        const response = await SalesPersonTeusService.getAssignmentsData(payload);

        if (response.status) {
          // Initialize _showDetails property for each item
          const data = response.data.assignments.map(item => {
            const newItem = Object.assign({}, item);
            newItem._showDetails = false;
            return newItem;
          });

          this.$emit('data-loaded', {
            data: data,
            pagination: response.data.pagination,
            summary: response.data.summary
          });
          this.totalItems = response.data.pagination.totalItems;
          this.totalPages = response.data.pagination.totalPages;
        }
      } catch (error) {
        console.error('Error loading assignments data:', error);
        this.$emit('data-loaded', {
          data: [],
          pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: this.pagination.perPage },
          summary: { totalRecords: 0 }
        });
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
      }
    },

    refreshData() {
      this.loadData();
    },
  },

  mounted() {
    this.loadData();
  },

  watch: {
    assignmentsData: {
      handler() {
        // Reset pagination when data changes
        this.pagination.currentPage = 1;
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.formCardDash {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.expanded-details {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  padding: 15px;
}

.expanded-details .row {
  margin-bottom: 10px;
}

.expanded-details h6 {
  margin-bottom: 10px;
  font-weight: 600;
}

.expanded-details table.table-sm {
  font-size: 12px;
}

.expanded-details table.table-sm td {
  padding: 4px 8px;
  border: none;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #007bff !important;
  color: white !important;
  z-index: 10;
  border-color: #0056b3;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.table thead th {
  border-bottom: 2px solid #0056b3;
}

.table tbody td {
  vertical-align: middle;
  padding: 8px;
}

.table tbody td.text-center {
  text-align: center;
}

.table tbody td.text-right {
  text-align: right;
}

.badge {
  font-size: 11px;
}

.btn-outline-info {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.toggle-btn {
  min-width: 40px;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .expanded-details .col-md-6 {
    margin-bottom: 20px;
  }

  .table-responsive {
    max-height: 400px;
  }
}
</style>


// WEBPACK FOOTER //
// AssignmentSection.vue?5f1d0ec0